import { isEmpty, isSSN, isTFN, isEmail, formatMobilePhone, isValidIsraelID } from '@jjj/components';
import { isMobilePhone } from '../../../helpers/validators';
import actions from './actions';
import { createReducer, getError } from '../../../helpers/reducer';
import { keyBy } from '../../../helpers/core';
import { isAUS, getCurrentCountryContext, isISR, isUSA, isPostalCode } from '../../../helpers/countriesHelpers';
import { t } from '../../../i18n';

const initState = {
  talent: {
    availabilities: {
      data: [],
      loading: false,
    },
    notifications: {
      notify_update_availability_reminder: [],
    },
    skills: [],
    worker_score: 0,
  },
  editForm: null,
  isEdit: false,
  loading: false,
  deleted: false,
  formInfo: {
    selectedValue: '',
    countries: [],
  },
  isTerminationModalShown: false,
  termination_reason: null,
  do_not_rehire: false,
  isChangeStatusPending: false,
};

function validate(attr, value, state) {
  let error = '';
  const { formInfo } = state;

  if (isUSA() && attr === 'ssn' && !formInfo?.hidden_fields?.includes('ssn')) {
    if (isEmpty(value, true)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.talent.social_security_number_label') });
    } else if (!isSSN(value)) {
      error = t('dash.validation.valid_error', { fieldName: t('dash.talent.social_security_number_label') });
    }
  }

  if (isISR() && attr === 'israeli_id' && !formInfo?.hidden_fields?.includes('israeli_id')) {
    if (isEmpty(value, true)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.talent.israel_national_id_empty_error_label') });
    } else if (!isValidIsraelID(value)) {
      error = t('dash.validation.valid_error', { fieldName: t('dash.talent.israel_national_id_label') });
    }
  }

  if (isAUS() && attr === 'tax_information' && !formInfo?.hidden_fields?.includes('tax_information')) {
    if (isEmpty(value, true)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.talent.tax_file_number_label') });
    } else if (!isTFN(value)) {
      error = t('dash.validation.valid_error', { fieldName: t('dash.talent.tax_file_number_label') });
    }
  }

  if (attr === 'first_name') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.talent.first_name_label') });
    }
  }

  if (attr === 'last_name') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.talent.last_name_label') });
    }
  }

  if (attr === 'email') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.talent.email_label') });
    } else if (!isEmail(value)) {
      error = t('dash.validation.valid_error', { fieldName: t('dash.talent.email_label') });
    }
  }

  if (attr === 'phone_number') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.talent.phone_number_label') });
    } else if (!isMobilePhone(formatMobilePhone(value), getCurrentCountryContext().alpha_2_code)) {
      error = t('dash.validation.valid_error', { fieldName: t('dash.talent.phone_number_label') });
    }
  }

  if (attr === 'zip') {
    if (!isPostalCode(value)) {
      error = t('dash.validation.valid_error', { fieldName: t('dash.common.location.postal_code') });
    }
  }

  return error;
}

function onLoad(state) {
  return {
    ...state,
    loading: true,
    isEdit: false,
  };
}

function changeState(state, { payload }) {
  return { ...state, ...payload };
}

function onLoadAvailabilities(state) {
  return {
    ...state,
    talent: {
      ...state.talent,
      availabilities: {
        ...state.talent.availabilities,
        loading: true,
      },
    },
  };
}

function onLoadAvailabilitiesSuccess(state, action) {
  const {
    payload: { data },
  } = action;

  return {
    ...state,
    talent: {
      ...state.talent,
      availabilities: {
        ...state.talent.availabilities,
        ...data,
        data: {
          ...state.talent.availabilities.data,
          ...data.data,
        },
        loading: false,
      },
    },
  };
}

function onLoadSuccess(state, action) {
  const {
    payload: { data },
  } = action;

  return {
    ...state,
    talent: {
      ...state.talent,
      ...data,
      address: '',
    },
    isChangeStatusPending: false,
    loading: false,
  };
}

function onClearStore() {
  return {
    ...initState,
  };
}

function onEdit(state, { payload }) {
  const skills = payload.talent.skills.map(item => {
    return {
      ...item,
      _destroy: false,
    };
  });

  const talent = {
    ...payload.talent,
    skills,
    ...(isISR() ? { israeli_id: payload.talent.israeli_id || '' } : {}),
  };
  const propsArray = Object.keys(talent);

  const editForm = keyBy(
    propsArray,
    prop => prop,
    prop => ({
      name: prop,
      value:
        talent[prop] && prop !== 'tax_information' && Object.prototype.hasOwnProperty.call(talent[prop], 'id')
          ? talent[prop].id
          : talent[prop],
      error: validate(
        prop,
        prop === 'tax_information' ? talent[prop] && talent[prop].tax_file_number : talent[prop],
        state,
      ),
      touched: false,
    }),
  );

  return {
    ...state,
    isEdit: true,
    editForm,
  };
}

function onSaveSuccess(state, { payload }) {
  const { data } = payload;
  const talent = data;
  const status = state.formInfo.statuses && state.formInfo.statuses.find(item => item.id === talent.status);
  const birthday = data.birthday ? data.birthday.slice(0, 10) : null;

  talent.birthday = birthday;

  return {
    ...state,
    talent: {
      ...talent,
      status,
    },
    isEdit: false,
    editForm: null,
  };
}

function onSaveError(state, { payload }) {
  const { data } = payload;

  const propsArray = Object.entries(state.editForm);
  const editForm = keyBy(
    propsArray,
    entry => entry[0],
    entry => {
      const [fieldName, value] = entry;

      if (fieldName === 'skills' && data?.competencies) {
        const skillsIndexes = Object.keys(data.competencies);

        skillsIndexes.forEach(skillIndex => {
          value.value[skillIndex].error = data.competencies[skillIndex].position_id;
        });
      }

      return {
        ...value,
        error: getError(fieldName, data),
        touched: true,
      };
    },
  );

  return {
    ...state,
    editForm,
  };
}

function onDeleteSuccess(state) {
  return {
    ...state,
    isEdit: false,
    editForm: null,
    deleted: true,
  };
}

function onDeleteSkill(state, { payload }) {
  const { skillId } = payload;
  const skillsArray = state.editForm.skills.value;
  const index = skillsArray.findIndex(item => {
    return item.id === skillId;
  });

  if (skillsArray?.[index]?.newSkill) {
    skillsArray.splice(index, 1);
  } else {
    skillsArray[index] = {
      ...skillsArray[index],
      _destroy: true,
    };
  }

  return {
    ...state,
    editForm: {
      ...state.editForm,
      skills: {
        ...state.editForm.skills,
        value: skillsArray,
      },
    },
  };
}

function onAddSkillOnEditPage(state) {
  const { editForm } = state;
  const { candidate_or_above, skills } = editForm;
  const { value } = skills;

  if (!candidate_or_above.value) {
    value.push({
      id: new Date().getTime().toString(),
      position_name: '',
      position_id: '',
      _destroy: false,
      newSkill: true,
    });
  } else {
    value.push({
      id: new Date().getTime().toString(),
      position_name: '',
      position_id: '',
      level: '',
      _destroy: false,
      newSkill: true,
    });
  }

  editForm.skills = {
    ...state.editForm.skills,
    value,
  };

  return {
    ...state,
    editForm,
  };
}

function onChangeProperty(state, { payload }) {
  const { attr, value } = payload;
  const { editForm } = state;

  editForm[attr] = {
    value,
    error: validate(attr, value, state),
    name: attr,
    touched: true,
  };

  return {
    ...state,
    editForm: {
      ...editForm,
    },
  };
}

function onChangeSkill(state, { payload }) {
  const { skillId, selectedValue } = payload;
  const { editForm, formInfo } = state;
  const { value } = editForm.skills;
  const enumerableValue = formInfo.positions_levels.find(item => item.id === selectedValue) || '';

  if (enumerableValue) {
    value.forEach(item => {
      if (item.id === skillId) {
        item.position_name = enumerableValue.name;
        item.position_id = selectedValue;
        // eslint-disable-next-line prefer-destructuring
        item.level = enumerableValue.levels[0];
        item.error = null;
      }

      return item;
    });
  }

  editForm.skills = {
    value,
    error: '',
    name: 'skills',
    touched: true,
  };

  return {
    ...state,
    editForm: {
      ...editForm,
    },
  };
}

function onChangeLevel(state, { payload }) {
  const { levelId, selectedValue } = payload;
  const { editForm } = state;
  const { value } = editForm.skills;

  value.forEach(item => {
    if (item.id === levelId) {
      item.level = selectedValue;
    }

    return item;
  });

  editForm.skills = {
    value,
    error: '',
    name: 'skills',
    touched: true,
  };

  return {
    ...state,
    editForm: {
      ...editForm,
    },
  };
}

function onChangeTaxInformation(state, { payload }) {
  const { editForm } = state;
  const { value } = payload;

  editForm.tax_information = {
    value: { ...editForm.tax_information.value, tax_file_number: value },
    error: validate('tax_information', value, state),
    name: 'tax_information',
    touched: true,
  };

  return {
    ...state,
    editForm: {
      ...editForm,
    },
  };
}

function onChangeProperties(state, { payload }) {
  const { attributes } = payload;
  const propsArray = Object.keys(attributes);

  const editFormProps = keyBy(
    propsArray,
    prop => prop,
    prop => ({
      name: prop,
      value: attributes[prop],
      error: validate(prop, attributes[prop], state),
      touched: true,
    }),
  );

  const editForm = {
    ...state.editForm,
    ...editFormProps,
  };

  return {
    ...state,
    editForm,
  };
}

function onCancel(state) {
  return {
    ...state,
    isEdit: false,
    editForm: null,
  };
}

function changeStatus(state) {
  return { ...state, isChangeStatusPending: true };
}

function onTouch(state, { payload }) {
  const propsArray = Object.entries(payload.editForm);
  const editForm = keyBy(
    propsArray,
    entry => entry[0],
    entry => {
      const [, value] = entry;

      return {
        ...value,
        touched: true,
      };
    },
  );

  return {
    ...state,
    editForm,
  };
}

function onLoadFormData(state, { payload }) {
  const { data } = payload;

  return {
    ...state,
    formInfo: data,
  };
}

function onShowTerminationModal(state) {
  const { isTerminationModalShown } = state;

  return {
    ...state,
    isTerminationModalShown: !isTerminationModalShown,
  };
}

function onResendContractsRequest(state) {
  return {
    ...state,
    isContractsResending: true,
  };
}

function onResendContractsSuccess(state) {
  return {
    ...state,
    isContractsResending: false,
  };
}

function onResendContractsError(state) {
  return {
    ...state,
    isContractsResending: false,
  };
}

export { initState, validate };
export default createReducer(initState, {
  [actions.ADD_SKILL_EDIT_PAGE]: onAddSkillOnEditPage,
  [actions.CANCEL]: onCancel,
  [actions.CHANGE_LEVEL]: onChangeLevel,
  [actions.CHANGE_PROPERTIES]: onChangeProperties,
  [actions.CHANGE_PROPERTY]: onChangeProperty,
  [actions.CHANGE_SKILL]: onChangeSkill,
  [actions.CHANGE_STATE]: changeState,
  [actions.CHANGE_STATUS]: changeStatus,
  [actions.CHANGE_TAX_INFORMATION]: onChangeTaxInformation,
  [actions.CLEAR_STORE]: onClearStore,
  [actions.DELETE_SKILL]: onDeleteSkill,
  [actions.DELETE_SUCCESS]: onDeleteSuccess,
  [actions.EDIT]: onEdit,
  [actions.LOAD]: onLoad,
  [actions.LOAD_AVAILABILITIES]: onLoadAvailabilities,
  [actions.LOAD_AVAILABILITIES_SUCCESS]: onLoadAvailabilitiesSuccess,
  [actions.LOAD_FORM_DATA]: onLoadFormData,
  [actions.LOAD_SUCCESS]: onLoadSuccess,
  [actions.SAVE_ERROR]: onSaveError,
  [actions.SAVE_SUCCESS]: onSaveSuccess,
  [actions.TOGGLE_TERMINATION_MODAL]: onShowTerminationModal,
  [actions.TOUCH]: onTouch,
  [actions.RESEND_CONTRACTS_REQUEST]: onResendContractsRequest,
  [actions.RESEND_CONTRACTS_SUCCESS]: onResendContractsSuccess,
  [actions.RESEND_CONTRACTS_ERROR]: onResendContractsError,
});
